import type { TeammateId } from "@repo/app-types/entities/teammate";
import type { FC } from "react";
import { Outlet, useMatch } from "react-router";
import { twMerge } from "tailwind-merge";
import { appPaths } from "../../../app/appPaths.js";
import { FullHeightPageContent } from "../../../components/layout/pageLayout.js";
import { AgentConfigAssistantSidebar } from "../agentConfigAssistantSidebar.js";
import { AgentConfigToolbar } from "../agentConfigToolbar.js";
import { AgentSimulationSlideover } from "../agentSimulationSlideover.js";
import { AttachmentEditorContainer } from "../components/attachmentEditorContainer.js";
import { useTeammateAgentConfigController } from "../useTeammateAgentConfigController.js";
import { SectionSuggestionListContainer } from "./sectionSuggestionList.js";
import { SectionTestingProvider } from "./sectionTesting.js";
import { StaticAgentConfigProvider } from "./staticAgentConfigContext.js";

export interface StaticAgentConfigLayoutProps {
  teammateAgentId: TeammateId;
}

/**
 * Layout for a static agent: the active tab content (Performance, Calls,
 * Instructions, Configuration, Testing) swaps inside the `<Outlet />`.
 * The AI Assistant panel shows on the Instructions, Configuration, and
 * Testing tabs; the toolbar (Try this agent / Publish / …) shows only on
 * Instructions and Configuration. Performance and Calls are read-only and
 * render like a regular agent's tabs. The Instructions and Configuration
 * tabs consume the controller via context.
 */
export const StaticAgentConfigLayout: FC<StaticAgentConfigLayoutProps> = ({
  teammateAgentId,
}) => {
  const controller = useTeammateAgentConfigController(teammateAgentId);
  const {
    editingAttachment,
    setEditingAttachment,
    pendingPatch,
    setPendingPatch,
  } = controller;

  // Call each matcher unconditionally so hook order stays stable, then combine.
  const isInstructionsTab = useMatch(
    appPaths.teammateAgentInstructions.pattern,
  );
  const isResourcesOutputsTab = useMatch(
    appPaths.teammateAgentResourcesOutputs.pattern,
  );
  const isTestingTab = useMatch(`${appPaths.teammateAgentTesting.pattern}/*`);
  // The AI Assistant shows on the editing + testing tabs; the toolbar (Try this
  // agent / Publish / …) shows only on the editing tabs, not on Testing.
  const showAssistant = Boolean(
    isInstructionsTab || isResourcesOutputsTab || isTestingTab,
  );
  const showToolbar = Boolean(isInstructionsTab || isResourcesOutputsTab);

  const sidebar = (
    <AgentConfigAssistantSidebar
      controller={controller}
      aboveComposer={<SectionSuggestionListContainer />}
    />
  );

  // Performance/Calls carry no config chrome, so render them the same way a
  // regular agent's tabs are rendered — each tab owns its layout (sticky
  // filter bar + PageBody) inside the page's scroll region, not the
  // full-width editing layout below.
  const content = !showAssistant ? (
    <Outlet />
  ) : (
    // Declared full height so the page shell provides a bounded scroll
    // region this fills exactly; inner areas scroll instead of the page —
    // otherwise the page scrolls and the toolbar scrolls out of view.
    <FullHeightPageContent>
      <div className="flex h-full min-w-[1100px] flex-col">
        {editingAttachment ? (
          <AttachmentEditorContainer
            key={editingAttachment.attachmentId}
            knowledgeBaseId={editingAttachment.knowledgeBaseId}
            attachmentId={editingAttachment.attachmentId}
            pendingPatch={pendingPatch}
            onPatchApplied={() => setPendingPatch(null)}
            onClose={() => {
              setEditingAttachment(null);
              setPendingPatch(null);
            }}
            sidebar={sidebar}
          />
        ) : (
          <>
            {showToolbar ? (
              <AgentConfigToolbar controller={controller} />
            ) : null}
            <div className="flex min-h-0 flex-1">
              {/* Instructions/Resources are full-bleed editing surfaces that
                manage their own padding; Testing is a normal content tab that
                brings its own PageBody padding and scrolls here so the toolbar
                and sidebar stay put. `relative`: react-aria's hidden inputs
                must count toward this pane's overflow, not the page shell's. */}
              <div
                className={twMerge(
                  "relative min-w-0 flex-1",
                  isTestingTab ? "overflow-y-auto" : "overflow-hidden",
                )}
              >
                <Outlet />
              </div>
              {sidebar}
            </div>
          </>
        )}

        <AgentSimulationSlideover controller={controller} />
      </div>
    </FullHeightPageContent>
  );

  // The testing provider wraps the whole layout (not each branch) so the
  // AI Assistant's checks and section suggestions persist across tab switches.
  return (
    <StaticAgentConfigProvider controller={controller}>
      <SectionTestingProvider controller={controller}>
        {content}
      </SectionTestingProvider>
    </StaticAgentConfigProvider>
  );
};
